// Balkon-Werkzeichnung: Geometrie (Grundriss mit 90°-Ecken), Pfostenaufteilung, Elemente (ASTRAL), Treppe, Stückliste
export const POST_PROFILES = {
  "75x75": { along: 75, across: 75, plate: 135, feet: { top: true, side: true } },
  "80x50": { along: 80, across: 50, plate: null, feet: { top: false, side: true } },
  "100x100": { along: 100, across: 100, plate: 160, feet: { top: true, side: true } },
  "120x120": { along: 120, across: 120, plate: 180, feet: { top: true, side: true } },
  "150x150": { along: 150, across: 150, plate: 210, feet: { top: true, side: true } },
  "200x100": { along: 200, across: 100, plate: 260, feet: { top: true, side: true } },
};
export const MODELS = {
  ASTRAL: { type: "vertical", stabW: 40, stabD: 20, gap: 90, gaps: [90, 50], traeger: "Trägerprofil 45x35", einfass: "Einfassprofil 30x26", stab: "Profil 40x20", stabDeduct: 95 },
  GALACTIC: { type: "vertical", variant: "galactic", stabW: 30, stabD: 40, gap: 30, gaps: [30, 20], traeger: "Trägerprofil 45x35", einfass: null, noEinfass: true, stab: "Latte 30x40", stabDeduct: 5, overhang: 200 },
  LUNAR: { type: "horizontal", lamH: 40, lamD: 20, gap: 20, gaps: [20], einfass: "Einfassprofil 30x26", einfassW: 26, lam: "Profil 40x20", lamDeduct: 5 },
  ECLIPSE: { type: "horizontal", variant: "eclipse", lamH: 110, lamD: 15, angle: 27, overlap: 5, gap: 5, gaps: [5], einfass: "H-Profil 45x30", einfassW: 45, lam: "Lamelle 110x15", lamDeduct: 20 },
};
// ECLIPSE: schräge Lamellen (110x15) mit Überlappung ≥ overlap → max. Steigung (lamH − overlap); sonst gleichmäßige Lamellen mit Abstand gap
export const lamCount = (H, m) => m.variant === "eclipse"
  ? Math.max(2, Math.ceil((H - m.lamH) / (m.lamH - (m.overlap || 5))) + 1)
  : Math.max(1, Math.round((H + m.gap) / (m.lamH + m.gap)));
export const BAR_LENGTH = 6000, SAW = 4;

export const newSide = (label, length) => ({ id: crypto.randomUUID(), label, length, fence: true, inset: null, openings: [], angle: 90, corner: "auto", lichten: "" });
export const parseLichten = (txt) => String(txt || "").split(/[,;\s]+/).map(Number).filter((v) => v > 0);
export const isBendCorner = (sd) => Number(sd.angle || 90) !== 90 && (sd.corner === "eckfeld" || (sd.corner !== "post" && Number(sd.angle) >= 135));
export const HANDRAILS = { "66x53": { w: 66, h: 53 } };
export const newOpening = (from) => ({ id: crypto.randomUUID(), from, width: 1000, stairs: { angle: 28.63, gap: 90, elementHeight: 940, mount: "top", fieldsTxt: "1507, 1633, 1619", postsTxt: "1260, 1260, 1260, 1260", tread: 300 } });

export function emptyBalkon() {
  return {
    kunde: "", kommission: "", farbe: "RAL 7016 - Anthrazitgrau",
    model: "ASTRAL", gap: 90, elementHeight: 940, bottom: 60, abzug: 2, minEdge: 35, clearance: 5,
    post: { profil: "75x75", height: 1020, mount: "top", inset: 20, houseGap: 0, openingGap: 3, sideOffset: 50 },
    maxField: 2000, startEnd: "house", endEnd: "house",
    handrail: { enabled: false, profile: "66x53", gap: 100, top: 20 }, extras: "",
    sides: [newSide("A", 1066), newSide("B", 4510), newSide("C", 1051)],
    fieldOverride: {},
  };
}

export function stabLayout(W, gap, stabW, minEdge) {
  let n = Math.floor((W + gap) / (stabW + gap));
  const edge = (k) => (W - k * stabW - (k - 1) * gap) / 2;
  while (n > 1 && edge(n) < minEdge) n--;
  n = Math.max(0, n);
  return { n, edge: n ? Math.round(edge(n) * 10) / 10 : 0 };
}

// Pfostenpositionen entlang einer Seite (lokal s: 0..L ab Außenkante Anfang, t: Einrückung von Außenkante)
export function computeBalkon(plan) {
  const prof = POST_PROFILES[plan.post.profil] || POST_PROFILES["75x75"];
  const P = prof.along, model = MODELS[plan.model] || MODELS.ASTRAL;
  const sides = plan.sides, n = sides.length;
  const isAstral = plan.model === "ASTRAL";
  const cornerPW = (i) => (isAstral && sides[i]?.corner === "profil45") ? 45 : P;
  const insetOf = (i) => (sides[i]?.inset ?? plan.post.inset);
  const posts = [], fields = [], runs = [], stairs = [];
  let zf = 1;
  const pid = (s) => `${s}`;
  const bendAfter = (i) => i >= 0 && i < n - 1 && sides[i].fence && sides[i + 1].fence && isBendCorner(sides[i]);
  const startGap = (i) => (bendAfter(i - 1) ? null : i > 0 && sides[i - 1].fence ? insetOf(i - 1) : plan.startEnd === "house" ? plan.post.houseGap : insetOf(i));
  const endGap = (i) => (bendAfter(i) ? null : i < n - 1 && sides[i + 1].fence ? insetOf(i + 1) : plan.endEnd === "house" ? plan.post.houseGap : insetOf(i));
  // Seitenlänge: manuell (Lichten je Feld) → Außenmaß = Gaps + Pfosten + Σ Lichte
  const lenOf = (i) => {
    const li = parseLichten(sides[i].lichten);
    if (!li.length || sides[i].openings?.length) return Number(sides[i].length) || 0;
    const sg = startGap(i), eg = endGap(i);
    return (sg === null ? 0 : sg + P) + li.reduce((a, b) => a + b, 0) + (li.length - 1) * P + (eg === null ? 0 : eg + P);
  };
  const dir = [0, -1]; let pos = [0, 0];
  const geom = []; // pro Seite: Startpunkt, Richtung, Normale (innen), Knickwinkel
  for (let i = 0; i < n; i++) {
    const d = [...dir], nrm = [-d[1], d[0]], L = lenOf(i);
    geom.push({ a: [...pos], d, nrm, L, angle: Number(sides[i].angle || 90), bend: bendAfter(i) });
    pos = [pos[0] + d[0] * L, pos[1] + d[1] * L];
    const th = ((180 - Number(sides[i].angle || 90)) * Math.PI) / 180; // Rechtsdrehung um Außenwinkel
    const nd = [d[0] * Math.cos(th) - d[1] * Math.sin(th), d[0] * Math.sin(th) + d[1] * Math.cos(th)];
    dir[0] = Math.round(nd[0] * 1e6) / 1e6; dir[1] = Math.round(nd[1] * 1e6) / 1e6;
  }
  const toXY = (i, s, t) => { const g = geom[i]; return [g.a[0] + g.d[0] * s + g.nrm[0] * t, g.a[1] + g.d[1] * s + g.nrm[1] * t]; };
  const addPost = (i, s1, t, type, pw = P) => { const p = { id: pid(posts.length + 1), side: i, s1, s2: s1 + pw, t, type, pw, xy: toXY(i, s1, t), height: plan.post.height }; posts.push(p); return p; };

  for (let i = 0; i < n; i++) {
    const sd = sides[i], L = geom[i].L, t = insetOf(i);
    if (!sd.fence) continue;
    const bendIn = bendAfter(i - 1), bendOut = bendAfter(i);
    const manual = !sd.openings?.length ? parseLichten(sd.lichten) : [];
    const ops = [...(sd.openings || [])].sort((a, b) => a.from - b.from);
    const bounds = [0, ...ops.flatMap((o) => [o.from, o.from + o.width]), L];
    for (let r = 0; r < bounds.length; r += 2) {
      const from = bounds[r], to = bounds[r + 1];
      const isFirst = r === 0, isLast = r === bounds.length - 2;
      const hasPrev = i > 0 && sides[i - 1].fence, hasNext = i < n - 1 && sides[i + 1].fence;
      // Startpfosten
      const sBend = isFirst && bendIn, eBend = isLast && bendOut; // Knick ohne Pfosten (Eckfeld)
      let start;
      if (sBend) start = { s1: -P, bend: true };
      else if (isFirst && hasPrev) start = { s1: insetOf(i - 1), shared: posts[posts.length - 1] };
      else if (isFirst) start = { s1: plan.startEnd === "house" ? plan.post.houseGap : t };
      else start = { s1: from + plan.post.openingGap };
      let end;
      if (eBend) end = { s1: L, bend: true };
      else if (isLast && hasNext) end = { s1: L - insetOf(i + 1) - cornerPW(i), pw: cornerPW(i) };
      else if (isLast) end = { s1: L - P - (plan.endEnd === "house" ? plan.post.houseGap : t) };
      else end = { s1: to - plan.post.openingGap - P };
      const sp = start.bend ? null : start.shared ? start.shared : addPost(i, start.s1, t, isFirst && !hasPrev ? "end-left" : "opening-right");
      if (start.shared) { sp.type = "corner"; sp.cornerSides = [i - 1, i]; }
      const cursor0 = start.bend ? 0 : start.shared ? start.s1 + (start.shared.pw || P) : sp.s2;
      const lichte = end.s1 - cursor0;
      const key = `${i}-${r / 2}`;
      let widths;
      if (manual.length) widths = manual;
      else {
        let nf = plan.fieldOverride?.[key] || 1;
        if (!plan.fieldOverride?.[key]) while ((lichte - (nf - 1) * P) / nf > plan.maxField) nf++;
        widths = Array.from({ length: nf }, () => (lichte - (nf - 1) * P) / nf);
      }
      const nf = widths.length;
      const runFields = [];
      let cursor = cursor0;
      for (let k = 0; k < nf; k++) {
        const f = widths[k];
        const bs = k === 0 && start.bend, be = k === nf - 1 && end.bend;
        const elem = Math.round(f - (bs ? 0 : plan.clearance) - (be ? 0 : plan.clearance));
        const st = model.type === "horizontal" ? { n: lamCount(plan.elementHeight, model), edge: 0 } : stabLayout(elem, plan.gap, model.stabW, plan.minEdge);
        const fld = { no: zf++, side: i, run: r / 2, from: cursor, to: cursor + f, lichte: Math.round(f * 10) / 10, width: elem, height: plan.elementHeight, stabs: st.n, edge: st.edge,
          bendStart: bs ? geom[i - 1].angle : null, bendEnd: be ? geom[i].angle : null };
        fields.push(fld); runFields.push(fld);
        cursor += f;
        if (k < nf - 1) { addPost(i, cursor, t, "mid"); cursor += P; }
      }
      const ep = end.bend ? null : addPost(i, end.s1, t, isLast && hasNext ? "corner" : isLast ? "end-right" : "opening-left", end.pw || P);
      if (ep && isLast && hasNext) { ep.cornerSides = [i, i + 1]; if (end.pw === 45) ep.kind = "profil45"; }
      runs.push({ key, side: i, from, to, lichte: Math.round(lichte), nf, fields: runFields, startPost: sp?.id, endPost: ep?.id, manual: manual.length > 0 });
      if (!isLast) {
        const o = ops[r / 2];
        const st = o.stairs || {};
        const ang = Number(st.angle) || 28.63, rad = (ang * Math.PI) / 180;
        const eh = Number(st.elementHeight) || plan.elementHeight;
        const sgap = Number(st.gap) || plan.gap;
        const flds = st.fieldsTxt ? parseLichten(st.fieldsTxt) : (st.length ? [st.length] : []);
        const ph = st.postsTxt ? parseLichten(st.postsTxt) : (st.lowerPostHeight ? [plan.post.height, st.lowerPostHeight] : []);
        let cum = 0;
        const sub = flds.map((len, k) => {
          const elem = Math.round(len - 2 * plan.clearance);
          const sl = model.type === "horizontal" ? { n: lamCount(eh, model), edge: 0 } : stabLayout(elem, sgap, model.stabW, plan.minEdge);
          const run = Math.round(len * Math.cos(rad)), drop = Math.round(len * Math.sin(rad));
          const f = { idx: k, length: len, element: elem, stabs: sl.n, edge: sl.edge, run, drop, fromRun: cum };
          cum += run; return f;
        });
        const totalRun = cum, totalDrop = Math.round(flds.reduce((a, l) => a + l, 0) * Math.sin(rad));
        stairs.push({ no: stairs.length + 1, side: i, opening: o.id, from: o.from, width: o.width || totalRun, angle: ang, gap: sgap, elementHeight: eh, mount: st.mount || "top",
          run: totalRun, drop: totalDrop, tread: st.tread || 300, steps: Math.max(1, Math.round(totalRun / (st.tread || 300))),
          upperPost: ep?.id, upperPostHeight: plan.post.height, postHeights: ph, sub });
      }
    }
  }
  // Fußtyp je Pfosten (Blickrichtung von außen: links/mitte/rechts, Ecke); 45x45-Verbinder ohne Platte
  for (const p of posts) {
    if (p.kind === "profil45") { p.foot = null; continue; }
    p.foot = p.type === "corner" ? "Ecke" : p.type === "mid" ? "Mitte" : p.type === "end-left" || p.type === "opening-right" ? "Links" : "Rechts";
  }
  // Stückliste
  const H = plan.elementHeight, horiz = model.type === "horizontal", stabLen = horiz ? null : H - model.stabDeduct;
  const cut = horiz ? { einfass: [], lam: [] } : (model.noEinfass ? { traeger: [], stab: [] } : { traeger: [], einfass: [], stab: [] });
  const bends = [];
  const stairCut = stairs.flatMap((s) => s.sub.map((sf) => ({ width: sf.element, stabs: sf.stabs, height: s.elementHeight })));
  for (const f of [...fields, ...stairCut]) {
    const w = f.width - plan.abzug, fh = f.height || H;
    const isBend = f.bendStart || f.bendEnd;
    if (horiz) {
      if (isBend) { if (!f.bendStart) cut.flach = [...(cut.flach || []), fh]; if (!f.bendEnd) cut.flach = [...(cut.flach || []), fh]; }
      else cut.einfass.push(fh, fh);
      const ded = isBend ? (f.bendStart ? 0 : 2) + (f.bendEnd ? 0 : 2) : model.lamDeduct;
      for (let k = 0; k < f.stabs; k++) cut.lam.push(w - ded);
    } else { cut.traeger.push(w, w); if (!model.noEinfass) cut.einfass.push(w, w); const sLen = fh - model.stabDeduct; for (let k = 0; k < f.stabs; k++) cut.stab.push(sLen); }
    if (f.bendEnd) bends.push({ zf: f.no, angle: f.bendEnd, length: fh });
  }
  const hr = plan.handrail?.enabled ? HANDRAILS[plan.handrail.profile] || HANDRAILS["66x53"] : null;
  const handrail = hr ? sides.map((sd, i) => ({ side: sd.label, length: Math.round(geom[i].L), miter: geom[i].angle !== 180 ? Math.round((geom[i].angle / 2) * 10) / 10 : null })).filter((_, i) => sides[i].fence) : [];
  const postHeightSuggest = hr ? plan.bottom + H + Number(plan.handrail.gap || 0) + hr.h + Number(plan.handrail.top || 0) : null;
  const postHeights = {};
  for (const p of posts) if (p.kind !== "profil45") postHeights[p.height] = (postHeights[p.height] || 0) + 1;
  for (const s of stairs) for (let k = 0; k < s.sub.length; k++) { const h = s.postHeights[k + 1] ?? s.postHeights[0] ?? 1260; postHeights[h] = (postHeights[h] || 0) + 1; }
  const verbinder45 = posts.filter((p) => p.kind === "profil45").length;
  const feet = {};
  for (const p of posts) if (p.foot) feet[p.foot] = (feet[p.foot] || 0) + 1;
  const stairPostCount = stairs.reduce((a, s) => a + s.sub.length, 0);
  if (stairPostCount) feet["Treppe"] = stairPostCount;
  const bars = Object.fromEntries(Object.entries(cut).map(([k, v]) => [k, packBars(v)]));
  const outline = geom.map((g) => g.a).concat([pos]);
  return { posts, fields, runs, stairs, geom, outline, cut, bars, postHeights, feet, verbinder45, model, prof, stabLen, bends, handrail, hr, postHeightSuggest, totalLength: Math.round(geom.reduce((a, g) => a + g.L, 0)) };
}

// First-fit-decreasing Zuschnitt auf 6000-mm-Stangen mit 4 mm Sägeschnitt
export function packBars(lengths, bar = BAR_LENGTH, saw = SAW) {
  const items = [...lengths].sort((a, b) => b - a);
  const bins = [];
  for (const l of items) {
    let b = bins.find((x) => x.rest >= l + saw);
    if (!b) { b = { rest: bar, parts: [] }; bins.push(b); }
    b.parts.push(l); b.rest -= l + saw;
  }
  const used = items.reduce((a, l) => a + l, 0);
  return { count: bins.length, pieces: items.length, total: used, waste: bins.length ? Math.round((1 - used / (bins.length * bar)) * 1000) / 10 : 0, bins: bins.map((b) => ({ parts: b.parts, rest: Math.round(b.rest) })) };
}
